<template>
  <div class="login-view min-h-screen flex flex-col lg:flex-row">
    
    <!-- Left Panel - Brand/Image Section (Hidden on mobile, visible on lg+) -->
    <div 
      class="hidden lg:flex lg:w-1/2 xl:w-2/5 relative bg-gradient-to-br from-light-accent via-[#6d4530] to-[#4a2f1f] overflow-hidden"
    >
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-10 dot-pattern"></div>
      
      <!-- Content -->
      <div class="relative z-10 flex flex-col justify-between p-8 xl:p-12 h-full w-full">
        <!-- Logo -->
        <router-link to="/" class="text-2xl font-bold text-white flex items-center gap-2">
          <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <CalendarDaysIcon class="w-6 h-6 text-white" />
          </div>
          <span>Gestor<span class="text-white/80">Citas</span></span>
        </router-link>
        
        <!-- Main Message -->
        <div class="flex-1 flex flex-col justify-center py-12">
          <h1 class="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            Bienvenido de nuevo.
          </h1>
          <p class="text-lg text-white/80 max-w-md mb-8">
            Accede a tu panel de control y continúa gestionando tus citas de manera eficiente.
          </p>
          
          <!-- Stats/Info -->
          <div class="grid grid-cols-2 gap-4 max-w-md">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div class="text-3xl font-bold text-white mb-1">5,000+</div>
              <div class="text-sm text-white/70">Profesionales activos</div>
            </div>
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div class="text-3xl font-bold text-white mb-1">50K+</div>
              <div class="text-sm text-white/70">Citas gestionadas</div>
            </div>
          </div>
        </div>
        
        <!-- Bottom Badge -->
        <div class="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 w-fit">
          <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <ShieldCheckIcon class="w-5 h-5 text-white" />
          </div>
          <div>
            <p class="text-white font-semibold text-sm">Acceso Seguro</p>
            <p class="text-white/60 text-xs">Tus datos están protegidos</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Right Panel - Form Section -->
    <div class="flex-1 flex flex-col bg-light-background dark:bg-dark-background min-h-screen">
      
      <!-- Mobile Header -->
      <div class="lg:hidden bg-gradient-to-r from-light-accent to-[#6d4530] p-4">
        <router-link to="/" class="text-xl font-bold text-white flex items-center gap-2">
          <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <CalendarDaysIcon class="w-5 h-5 text-white" />
          </div>
          <span>Gestor<span class="text-white/80">Citas</span></span>
        </router-link>
      </div>
      
      <!-- Form Container -->
      <div class="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        <div class="w-full max-w-md">
          
          <!-- Banner: worker_welcome (usuario existente invitado a empresa) -->
          <div
            v-if="welcomeCompany"
            class="mb-6 flex items-start gap-3 rounded-xl border border-light-border dark:border-dark-border bg-light-border/20 dark:bg-dark-border/20 px-4 py-3"
          >
            <div class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full accent-bg">
              <BuildingOfficeIcon class="h-4 w-4 text-white" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                Te han agregado a {{ welcomeCompany }}
              </p>
              <p class="mt-0.5 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                Iniciá sesión con tu cuenta existente para acceder al panel.
              </p>
            </div>
          </div>

          <!-- Header -->
          <div class="text-center mb-8">
            <h2 class="text-2xl sm:text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
              Inicia Sesión
            </h2>
            <p class="mt-2 text-light-text-secondary dark:text-dark-text-secondary">
              Ingresa tus credenciales para continuar
            </p>
          </div>
          
          <!-- Social Login -->
          <div class="mb-6">
            <button 
              type="button"
              @click="handleGoogleLogin"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-light-border dark:border-dark-border 
                     hover:border-light-accent/50 dark:hover:border-dark-accent/50 transition-colors text-light-text-primary dark:text-dark-text-primary"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span class="font-medium">Google</span>
            </button>
          </div>
          
          <!-- Divider -->
          <div class="relative mb-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-light-border dark:border-dark-border"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-4 bg-light-background dark:bg-dark-background text-light-text-secondary dark:text-dark-text-secondary">
                o continúa con email
              </span>
            </div>
          </div>
          
          <!-- Login Form -->
          <form @submit.prevent="handleLogin" class="space-y-5">
            
            <!-- Email Field -->
            <div>
              <label for="email" class="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
                Correo Electrónico
              </label>
              <div class="relative">
                <input 
                  id="email"
                  type="email"
                  required
                  v-model="form.email"
                  class="w-full px-4 py-3 rounded-xl bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary 
                         border-2 border-light-border dark:border-dark-border placeholder-light-text-secondary/60 dark:placeholder-dark-text-secondary/60 
                         focus:outline-none focus:border-light-accent dark:focus:border-dark-accent transition-all duration-200"
                  :class="{ 'border-red-400 focus:border-red-400': errors.email }"
                  placeholder="tu@email.com"
                />
                <EnvelopeIcon class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-secondary/50" />
              </div>
              <p v-if="errors.email" class="mt-1 text-xs text-red-500">{{ errors.email }}</p>
            </div>
            
            <!-- Password Field -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label for="password" class="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Contraseña
                </label>
                <router-link to="/forgot-password" class="text-sm text-light-accent hover:underline">
                  ¿Olvidaste tu contraseña?
                </router-link>
              </div>
              <div class="relative">
                <input 
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  v-model="form.password"
                  class="w-full px-4 py-3 pr-12 rounded-xl bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary 
                         border-2 border-light-border dark:border-dark-border placeholder-light-text-secondary/60 dark:placeholder-dark-text-secondary/60 
                         focus:outline-none focus:border-light-accent dark:focus:border-dark-accent transition-all duration-200"
                  :class="{ 'border-red-400 focus:border-red-400': errors.password }"
                  placeholder="Ingresa tu contraseña"
                />
                <button 
                  type="button" 
                  @click="showPassword = !showPassword"
                  class="absolute right-4 top-1/2 -translate-y-1/2 text-light-text-secondary/50 hover:text-light-accent transition-colors"
                >
                  <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.password" class="mt-1 text-xs text-red-500">{{ errors.password }}</p>
            </div>
            
            <!-- Remember Me -->
            <div class="flex items-center gap-3">
              <input 
                id="rememberMe"
                type="checkbox"
                v-model="form.rememberMe"
                class="h-4 w-4 rounded border-light-border text-light-accent focus:ring-light-accent/50"
              />
              <label for="rememberMe" class="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Mantener sesión iniciada
              </label>
            </div>
            
            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="authStore.isLoading"
              class="w-full bg-[#8c5a3d] hover:bg-[#704832] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8c5a3d]/40 text-white py-3.5 rounded-xl font-semibold text-base 
                     transition-all duration-200 shadow-lg shadow-[#8c5a3d]/25 hover:shadow-xl hover:shadow-[#8c5a3d]/30
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <LoadingSpinner v-if="authStore.isLoading" class="w-5 h-5" />
              <template v-else>
                Iniciar Sesión
                <ArrowRightIcon class="w-5 h-5" />
              </template>
            </button>
          </form>
          
          <!-- Register Link -->
          <p class="text-center mt-8 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            ¿Aún no tienes una cuenta?
            <router-link to="/register" class="text-light-accent font-semibold hover:underline ml-1">
              Regístrate Gratis
            </router-link>
          </p>

        </div>
      </div>
      
      <!-- Error Toast -->
      <Transition name="fade">
        <div 
          v-if="authStore.error" 
          class="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 shadow-lg z-50"
        >
          <div class="flex items-start gap-3">
            <ExclamationTriangleIcon class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div class="flex-1">
              <p class="text-sm font-medium text-red-800 dark:text-red-200">Error al iniciar sesión</p>
              <p class="text-sm text-red-600 dark:text-red-300 mt-1">{{ authStore.error }}</p>
            </div>
            <button @click="authStore.clearError" class="text-red-400 hover:text-red-600">
              <XMarkIcon class="w-5 h-5" />
            </button>
          </div>
        </div>
      </Transition>
      
    </div>
    
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  XMarkIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon
} from '@heroicons/vue/24/outline'

import { useAuthStore } from '@/stores/auth'
import { API_BASE_URL } from '@/services/api'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { isNativeApp } from '@/utils/platform'

// Setup
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const welcomeCompany = computed(() =>
  route.query.welcome === '1' ? (route.query.company || '') : ''
)

// State
const showPassword = ref(false)

const form = reactive({
  email: '',
  password: '',
  rememberMe: false
})

const errors = reactive({
  email: '',
  password: ''
})

// Clear errors
const clearErrors = () => {
  errors.email = ''
  errors.password = ''
}

// Validate form
const validateForm = () => {
  clearErrors()
  let isValid = true

  if (!form.email.trim()) {
    errors.email = 'El correo es obligatorio'
    isValid = false
  } else if (!form.email.includes('@') || !form.email.includes('.')) {
    errors.email = 'Introduce un correo válido'
    isValid = false
  }

  if (!form.password) {
    errors.password = 'La contraseña es obligatoria'
    isValid = false
  } else if (form.password.length < 6) {
    errors.password = 'Mínimo 6 caracteres'
    isValid = false
  }

  return isValid
}

// Handle login
const handleLogin = async () => {
  if (!validateForm()) return

  authStore.clearError()

  try {
    await authStore.login({
      email: form.email,
      password: form.password,
      rememberMe: form.rememberMe
    })

    if (authStore.isAuthenticated) {
      await router.push('/dashboard')
    }
  } catch (error) {
    console.error('Error en login:', error)
  }
}

// Redirigir al backend para iniciar flujo Google OAuth.
// En la app nativa, un window.location.href navegaría el WebView entero fuera
// de la app: abrimos un browser in-app y le pedimos al backend (platform=native)
// que redirija de vuelta con el custom scheme en vez de la URL https del front.
const handleGoogleLogin = async () => {
  if (!API_BASE_URL) {
    authStore.error = 'No está configurada la URL del backend'
    return
  }

  const authUrl = `${API_BASE_URL}/auth/google${isNativeApp() ? '?platform=native' : ''}`

  if (isNativeApp()) {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url: authUrl })
    return
  }

  window.location.href = authUrl
}

// Lifecycle
onMounted(() => {
  authStore.clearError()

  if (authStore.isAuthenticated) {
    router.push('/dashboard')
  }
})
</script>

<style scoped>
.login-view {
  --light-background: #fdfcfa;
  --light-text-primary: #1f1d20;
  --light-text-secondary: #5f5f5f;
  --light-accent: #8c5a3d;
  --light-border: #e0e0e0;
  --light-cta-bg: #353940;
  --light-cta-text: #fdfcfa;
  --dark-background: #1f1d20;
  --dark-background-modal: #2f2c30;
  --dark-text-primary: #fdfcfa;
  --dark-text-secondary: #848484;
  --dark-accent: #8c5a3d;
  --dark-border: #353940;
  --dark-cta-bg: #8c5a3d;
  --dark-cta-text: #fdfcfa;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dot pattern for left panel */
.dot-pattern {
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.3) 1px, transparent 1px);
  background-size: 40px 40px;
}
</style>
